'use client';

import { useEffect, useState } from 'react';
import {
  resolveThemePreference,
  THEME_STORAGE_KEY,
  type ThemeChangeDetail,
  type ThemeMode,
  type ThemePreference,
} from '@/lib/theme';

type ThemeChangeEvent = CustomEvent<ThemeChangeDetail>;

function MoonIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path
        d="M21.752 15.002A9.718 9.718 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 1 0 21.752 15.002Z"
        fill="currentColor"
      />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path
        d="M12 6.1a5.9 5.9 0 1 1 0 11.8 5.9 5.9 0 0 1 0-11.8Zm0-4.1c.41 0 .75.34.75.75v1.62a.75.75 0 0 1-1.5 0V2.75c0-.41.34-.75.75-.75Zm0 17.88c.41 0 .75.34.75.75v1.62a.75.75 0 0 1-1.5 0v-1.62c0-.41.34-.75.75-.75ZM4.36 3.3c.29 0 .57.11.79.33l1.14 1.14a.75.75 0 1 1-1.06 1.06L4.09 4.69a.75.75 0 0 1 .27-1.23Zm14.41 14.41c.29 0 .57.11.8.33l1.14 1.14a.75.75 0 0 1-1.06 1.06l-1.14-1.14a.75.75 0 0 1 .26-1.23ZM2 12c0-.41.34-.75.75-.75h1.62a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 12Zm17.88 0c0-.41.34-.75.75-.75h1.62a.75.75 0 0 1 0 1.5h-1.62a.75.75 0 0 1-.75-.75ZM5.5 17.71a.75.75 0 0 1 .8.16.75.75 0 0 1 0 1.06l-1.14 1.14A.75.75 0 1 1 4.1 19l1.14-1.13a.75.75 0 0 1 .26-.16Zm13.01-13a.75.75 0 0 1 1.06 0l1.14 1.13a.75.75 0 1 1-1.06 1.06l-1.14-1.14a.75.75 0 0 1 0-1.05Z"
        fill="currentColor"
      />
    </svg>
  );
}

function getSystemTheme() {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function getStoredPreference(): ThemePreference {
  const storedPreference = window.localStorage.getItem(THEME_STORAGE_KEY);

  return storedPreference === 'light' || storedPreference === 'system' ? storedPreference : 'dark';
}

function applyTheme(preference: ThemePreference) {
  const theme = resolveThemePreference(preference, getSystemTheme() === 'light');
  document.documentElement.dataset.themePreference = preference;
  document.documentElement.dataset.theme = theme;

  return theme;
}

export default function ThemeToggle() {
  const [preference, setPreference] = useState<ThemePreference>('dark');
  const [theme, setTheme] = useState<ThemeMode>('dark');

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');

    function syncTheme(nextPreference?: ThemePreference) {
      const currentPreference = nextPreference ?? getStoredPreference();
      const currentTheme = resolveThemePreference(currentPreference, mediaQuery.matches);
      setPreference(currentPreference);
      setTheme(currentTheme);
    }

    function handleThemeChange(event: Event) {
      syncTheme((event as ThemeChangeEvent).detail?.preference);
    }

    function handleStorage(event: StorageEvent) {
      if (event.key !== THEME_STORAGE_KEY) {
        return;
      }

      syncTheme(
        event.newValue === 'light' || event.newValue === 'system' ? event.newValue : 'dark',
      );
    }

    function handleSystemThemeChange() {
      if (getStoredPreference() !== 'system') {
        return;
      }

      const resolvedTheme = applyTheme('system');
      window.dispatchEvent(
        new CustomEvent<ThemeChangeDetail>('cleanor-theme-change', {
          detail: { preference: 'system', theme: resolvedTheme },
        }),
      );
    }

    syncTheme();
    window.addEventListener('cleanor-theme-change', handleThemeChange as EventListener);
    window.addEventListener('storage', handleStorage);
    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      window.removeEventListener('cleanor-theme-change', handleThemeChange as EventListener);
      window.removeEventListener('storage', handleStorage);
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, []);

  function handleToggle() {
    const nextPreference: ThemePreference = theme === 'dark' ? 'light' : 'dark';
    const nextTheme = applyTheme(nextPreference);
    setPreference(nextPreference);
    setTheme(nextTheme);

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, nextPreference);
    } catch {
      // Ignore storage errors and still keep the active document theme.
    }

    window.dispatchEvent(
      new CustomEvent<ThemeChangeDetail>('cleanor-theme-change', {
        detail: { preference: nextPreference, theme: nextTheme },
      }),
    );
  }

  const isLight = theme === 'light';
  const label =
    preference === 'system'
      ? isLight
        ? 'System theme active. Switch to dark theme'
        : 'System theme active. Switch to light theme'
      : isLight
        ? 'Switch to dark theme'
        : 'Switch to light theme';

  return (
    <button
      aria-label={label}
      aria-pressed={isLight}
      className={`header-icon-button header-theme-button ${isLight ? 'header-theme-button-light' : ''}`}
      onClick={handleToggle}
      title={label}
      type="button"
    >
      {isLight ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}
